import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Loader2, ChevronLeft, ChevronRight, Eye, ArrowLeft,
  ArrowUpDown, ArrowUp, ArrowDown, X, BarChart3, Layers, Activity,
  FolderKanban, BookOpen, TrendingUp, Filter, Calendar, RotateCcw,
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { dashboardApi } from '../../services/api';
import { formatDate, formatDateTime, scoreColor, scoreBg } from '../../utils/format';
import PaginationBar from '../../components/PaginationBar';
import type {
  DashboardProject, DashboardDetailRun, DashboardChartPoint,
  GroupSummaryItem, Pagination,
} from '../../types';
import ValidationReportModal from './ValidationReportModal';

/* ───────── Helpers ───────── */

type SortDir = 'asc' | 'desc';

/* ───────── Time Frame Presets ───────── */

const TIME_FRAME_OPTIONS: { value: string; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7', label: 'Last 7 Days' },
  { value: 'last14', label: 'Last 14 Days' },
  { value: 'last30', label: 'Last 30 Days' },
  { value: 'last60', label: 'Last 60 Days' },
  { value: 'last90', label: 'Last 90 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_hour', label: 'Last Hour' },
  { value: 'last_8_hours', label: 'Last 8 Hours' },
  { value: 'last_16_hours', label: 'Last 16 Hours' },
  { value: 'last_24_hours', label: 'Last 24 Hours' },
  { value: 'custom', label: 'Custom Range' },
];

/* ───────── Component ───────── */

const InsightsPage: React.FC = () => {
  /* ── Which view ── */
  const [selectedProject, setSelectedProject] = useState<DashboardProject | null>(null);

  /* ── Dashboard list state ── */
  const [projects, setProjects] = useState<DashboardProject[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listCount, setListCount] = useState(0);
  const [listPages, setListPages] = useState(1);
  const [listPage, setListPage] = useState(1);
  const [listPerPage, setListPerPage] = useState(10);
  const [listSearch, setListSearch] = useState('');
  const [listSortCol, setListSortCol] = useState('last_run');
  const [listSortDir, setListSortDir] = useState<SortDir>('desc');
  const [counts, setCounts] = useState<{ projects: number; groups: number; rules: number }>({ projects: 0, groups: 0, rules: 0 });

  /* ── Project detail state ── */
  const [activeTab, setActiveTab] = useState<'detail' | 'summary'>('detail');
  const [detailRuns, setDetailRuns] = useState<DashboardDetailRun[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailCount, setDetailCount] = useState(0);
  const [detailPages, setDetailPages] = useState(1);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPerPage, setDetailPerPage] = useState(10);
  const [detailSortCol, setDetailSortCol] = useState('created_date');
  const [detailSortDir, setDetailSortDir] = useState<SortDir>('desc');
  const [chartData, setChartData] = useState<DashboardChartPoint[]>([]);
  const [showChart, setShowChart] = useState(false);
  const [groupSummary, setGroupSummary] = useState<GroupSummaryItem[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryCount, setSummaryCount] = useState(0);
  const [summaryPages, setSummaryPages] = useState(1);
  const [summaryPage, setSummaryPage] = useState(1);
  const [filterLists, setFilterLists] = useState<{ groups: string[]; assets: string[]; checkpoints: string[]; environments: string[] }>({ groups: [], assets: [], checkpoints: [], environments: [] });
  const [filterGroup, setFilterGroup] = useState('');
  const [filterAsset, setFilterAsset] = useState('');
  const [filterEnvironment, setFilterEnvironment] = useState('');
  const [filterCheckpoint, setFilterCheckpoint] = useState('');
  const [filterTimeFrame, setFilterTimeFrame] = useState('last30');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  /* Applied filters — only sent to API when user clicks Apply */
  const [appliedFilters, setAppliedFilters] = useState<{
    group: string; asset: string; environment: string; checkpoint: string;
    timeFrame: string; startDate: string; endDate: string;
  }>({ group: '', asset: '', environment: '', checkpoint: '', timeFrame: 'last30', startDate: '', endDate: '' });
  const filtersChanged = (
    filterGroup !== appliedFilters.group || filterAsset !== appliedFilters.asset ||
    filterEnvironment !== appliedFilters.environment || filterCheckpoint !== appliedFilters.checkpoint ||
    filterTimeFrame !== appliedFilters.timeFrame || filterStartDate !== appliedFilters.startDate ||
    filterEndDate !== appliedFilters.endDate
  );

  /* ── Report modal ── */
  const [reportResultId, setReportResultId] = useState<number | null>(null);
  const [reportMeta, setReportMeta] = useState<{ projectName: string; groupName: string } | null>(null);

  /* ────────────────── Fetch Dashboard List ────────────────── */

  const fetchDashboardList = useCallback(async () => {
    setListLoading(true);
    try {
      const params: Record<string, any> = {
        page: listPage,
        items_per_page: listPerPage,
        sort_col: listSortCol,
        sort_dir: listSortDir,
      };
      if (listSearch.trim()) params.project = listSearch.trim();

      const resp = await dashboardApi.list(params);
      const payload = resp.data.payload;
      const list = payload?.dashboard || [];
      setProjects(Array.isArray(list) ? list : []);
      const pagination: Pagination | undefined = payload?.pagination;
      if (pagination) {
        setListCount(pagination.count || 0);
        setListPages(pagination.pages || 1);
      } else {
        setListCount(list.length || 0);
        setListPages(1);
      }
      setCounts({
        projects: payload?.total_project?.project_count || 0,
        groups: payload?.total_group?.group_count || 0,
        rules: payload?.total_rules?.rules_count || 0,
      });
    } catch {
      setProjects([]);
      setListCount(0);
      setListPages(1);
    } finally {
      setListLoading(false);
    }
  }, [listPage, listPerPage, listSearch, listSortCol, listSortDir]);

  useEffect(() => {
    if (!selectedProject) fetchDashboardList();
  }, [fetchDashboardList, selectedProject]);

  /* ────────────────── Fetch Project Detail ────────────────── */

  const fetchDetailRuns = useCallback(async () => {
    if (!selectedProject) return;
    setDetailLoading(true);
    try {
      const params: Record<string, any> = {
        page: detailPage,
        items_per_page: detailPerPage,
        sort_col: detailSortCol,
        sort_dir: detailSortDir,
        report_type: 'dashboard',
        chart: showChart ? 'true' : 'false',
      };
      /* Apply time frame or custom date range */
      if (appliedFilters.timeFrame === 'custom') {
        if (appliedFilters.startDate) params.start_date = appliedFilters.startDate;
        if (appliedFilters.endDate) params.end_date = appliedFilters.endDate;
      } else {
        params.time_frame = appliedFilters.timeFrame || 'last30';
      }
      if (appliedFilters.group) params.group = appliedFilters.group;
      if (appliedFilters.asset) params.asset = appliedFilters.asset;
      if (appliedFilters.environment) params.env = appliedFilters.environment;
      if (appliedFilters.checkpoint) params.checkpoint = appliedFilters.checkpoint;

      const resp = await dashboardApi.project(selectedProject.project_id, params);
      const payload = resp.data.payload;
      setDetailRuns(payload?.dashboard_detail || []);
      const pagination: Pagination | undefined = payload?.pagination;
      if (pagination) {
        setDetailCount(pagination.count || 0);
        setDetailPages(pagination.pages || 1);
      }
      if (payload?.chart) setChartData(payload.chart);
      setFilterLists({
        groups: payload?.group_list || [],
        assets: payload?.dataasset_list || [],
        checkpoints: payload?.checkpoint_list || [],
        environments: payload?.environment_list || [],
      });
    } catch {
      setDetailRuns([]);
    } finally {
      setDetailLoading(false);
    }
  }, [selectedProject, detailPage, detailPerPage, detailSortCol, detailSortDir, showChart, appliedFilters]);

  useEffect(() => {
    if (selectedProject && activeTab === 'detail') fetchDetailRuns();
  }, [fetchDetailRuns, selectedProject, activeTab]);

  /* ────────────────── Fetch Group Summary ────────────────── */

  const fetchGroupSummary = useCallback(async () => {
    if (!selectedProject) return;
    setSummaryLoading(true);
    try {
      const params: Record<string, any> = {
        page: summaryPage,
        items_per_page: 10,
        report_type: 'group_summary',
        sort_col: 'last_run',
        sort_dir: 'desc',
      };
      /* Apply same time frame filters as detail tab */
      if (appliedFilters.timeFrame === 'custom') {
        if (appliedFilters.startDate) params.start_date = appliedFilters.startDate;
        if (appliedFilters.endDate) params.end_date = appliedFilters.endDate;
      } else {
        params.time_frame = appliedFilters.timeFrame || 'last30';
      }
      const resp = await dashboardApi.project(selectedProject.project_id, params);
      const payload = resp.data.payload;
      setGroupSummary(payload?.group_summary || []);
      const pagination: Pagination | undefined = payload?.pagination;
      if (pagination) {
        setSummaryCount(pagination.count || 0);
        setSummaryPages(pagination.pages || 1);
      }
    } catch {
      setGroupSummary([]);
    } finally {
      setSummaryLoading(false);
    }
  }, [selectedProject, summaryPage, appliedFilters]);

  useEffect(() => {
    if (selectedProject && activeTab === 'summary') fetchGroupSummary();
  }, [fetchGroupSummary, selectedProject, activeTab]);

  /* ────────────────── Handlers ────────────────── */

  const handleSelectProject = (proj: DashboardProject) => {
    setSelectedProject(proj);
    setActiveTab('detail');
    setDetailPage(1);
    setShowChart(false);
    setChartData([]);
    setFilterGroup('');
    setFilterAsset('');
    setFilterEnvironment('');
    setFilterCheckpoint('');
    setFilterTimeFrame('last30');
    setFilterStartDate('');
    setFilterEndDate('');
    setAppliedFilters({ group: '', asset: '', environment: '', checkpoint: '', timeFrame: 'last30', startDate: '', endDate: '' });
    setSummaryPage(1);
  };

  const handleBack = () => {
    setSelectedProject(null);
    setDetailRuns([]);
    setGroupSummary([]);
    setChartData([]);
  };

  const handleViewReport = (run: DashboardDetailRun) => {
    const match = run.ge_report?.match(/\/(\d+)$/);
    if (match) {
      setReportResultId(parseInt(match[1], 10));
      setReportMeta({
        projectName: selectedProject?.project || '',
        groupName: run.group_name,
      });
    }
  };

  /* ────────────────── Sort helpers ────────────────── */

  function SortIcon({ col, activeCol, activeDir }: { col: string; activeCol: string; activeDir: SortDir }) {
    if (activeCol !== col) return <ArrowUpDown className="w-3.5 h-3.5 text-cargo-muted/40" />;
    return activeDir === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 text-cargo-accent" />
      : <ArrowDown className="w-3.5 h-3.5 text-cargo-accent" />;
  }

  /* ────────────────── Pagination ────────────────── */

  /* ══════════════════════════════════════════════════
     DASHBOARD LIST VIEW
     ══════════════════════════════════════════════════ */

  if (!selectedProject) {
    const handleListSort = (col: string) => {
      if (listSortCol === col) setListSortDir(d => d === 'asc' ? 'desc' : 'asc');
      else { setListSortCol(col); setListSortDir('asc'); }
      setListPage(1);
    };

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-cargo-accent" />
          <div>
            <h1 className="text-2xl font-bold text-cargo-text">Insights</h1>
            <p className="text-sm text-cargo-muted mt-0.5">Validation results and analytics across all projects</p>
          </div>
        </div>

        {/* Count Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Active Projects', value: counts.projects, icon: FolderKanban, color: 'text-blue-400' },
            { label: 'Active Groups', value: counts.groups, icon: Layers, color: 'text-purple-400' },
            { label: 'Total Rules', value: counts.rules, icon: BookOpen, color: 'text-emerald-400' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-cargo-surface rounded-xl border border-cargo-border p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-cargo-panel flex items-center justify-center">
                <Icon className={`w-5 h-5 ${color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-cargo-text">{value}</p>
                <p className="text-xs text-cargo-muted">{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="bg-cargo-surface rounded-xl border border-cargo-border p-4">
          <div className="relative flex-1 min-w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cargo-muted" />
            <input
              value={listSearch}
              onChange={(e) => { setListSearch(e.target.value); setListPage(1); }}
              placeholder="Search by project name..."
              className="w-full pl-10 pr-8 py-2.5 rounded-lg bg-cargo-panel border border-cargo-border
                         text-cargo-text placeholder-cargo-muted/50 text-sm
                         focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
            />
            {listSearch && (
              <button onClick={() => { setListSearch(''); setListPage(1); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-cargo-muted hover:text-cargo-text">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-cargo-surface rounded-xl border border-cargo-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-cargo-border">
                  {([
                    ['project', 'Project', true],
                    ['team_name', 'Team', true],
                    ['total_run', 'Total Runs', true],
                    ['last_run', 'Last Run', true],
                    ['dq_score', 'DQ Score', true],
                    ['br_score', 'B&R Score', true],
                    ['', '', false],
                  ] as [string, string, boolean][]).map(([col, label, sortable], i) => (
                    <th key={i}
                      className={`px-4 py-3 text-left text-xs font-medium text-cargo-muted uppercase tracking-wider
                                  ${sortable ? 'cursor-pointer hover:text-cargo-text select-none' : ''}`}
                      onClick={sortable ? () => handleListSort(col) : undefined}>
                      <div className="flex items-center gap-1">
                        {label}
                        {sortable && col && <SortIcon col={col} activeCol={listSortCol} activeDir={listSortDir} />}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {listLoading ? (
                  <tr><td colSpan={7} className="py-16 text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-cargo-accent mx-auto" />
                    <p className="text-cargo-muted text-sm mt-2">Loading dashboard...</p>
                  </td></tr>
                ) : projects.length === 0 ? (
                  <tr><td colSpan={7} className="py-16 text-center text-cargo-muted text-sm">No projects found.</td></tr>
                ) : (
                  projects.map((proj) => (
                    <tr key={proj.project_id}
                      className="border-b border-cargo-border/50 hover:bg-cargo-panel/50 transition-colors">
                      <td className="px-4 py-3">
                        <button onClick={() => handleSelectProject(proj)}
                          className="text-cargo-accent hover:underline font-medium text-sm text-left">
                          {proj.project}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-cargo-muted whitespace-nowrap">{proj.team_name}</td>
                      <td className="px-4 py-3 text-cargo-text text-center">{proj.total_run}</td>
                      <td className="px-4 py-3 text-cargo-muted whitespace-nowrap">{formatDate(proj.last_date)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded border ${scoreBg(proj.dq_score)}`}>
                          {(proj.dq_score || 0).toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {proj.br_score > 0 ? (
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded border ${scoreBg(proj.br_score)}`}>
                            {proj.br_score.toFixed(1)}%
                          </span>
                        ) : <span className="text-cargo-muted/40 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => handleSelectProject(proj)}
                          className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-accent hover:bg-cargo-accent/10 transition-colors"
                          title="View runs">
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!listLoading && projects.length > 0 && (
            <PaginationBar page={listPage} totalPages={listPages} perPage={listPerPage}
              onPageChange={setListPage} onPerPageChange={(n) => { setListPerPage(n); setListPage(1); }} />
          )}
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════
     PROJECT DETAIL VIEW
     ══════════════════════════════════════════════════ */

  const handleDetailSort = (col: string) => {
    if (detailSortCol === col) setDetailSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setDetailSortCol(col); setDetailSortDir('asc'); }
    setDetailPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Back + header */}
      <div>
        <button onClick={handleBack}
          className="flex items-center gap-1.5 text-sm text-cargo-muted hover:text-cargo-accent transition-colors mb-3">
          <ArrowLeft className="w-4 h-4" /> Back to Insights
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-6 h-6 text-cargo-accent" />
            <div>
              <h1 className="text-2xl font-bold text-cargo-text">{selectedProject.project}</h1>
              <p className="text-sm text-cargo-muted mt-0.5">
                Team: {selectedProject.team_name} &middot; DQ Score: <span className={scoreColor(selectedProject.dq_score)}>{(selectedProject.dq_score || 0).toFixed(1)}%</span>
              </p>
            </div>
          </div>
          <button onClick={() => { setShowChart(!showChart); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
              showChart ? 'bg-cargo-accent/10 border-cargo-accent text-cargo-accent' : 'bg-cargo-panel border-cargo-border text-cargo-muted hover:text-cargo-text'
            }`}>
            <TrendingUp className="w-4 h-4" />
            {showChart ? 'Hide Chart' : 'Show Chart'}
          </button>
        </div>
      </div>

      {/* Charts */}
      {showChart && chartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-cargo-surface rounded-xl border border-cargo-border p-4">
            <h3 className="text-sm font-medium text-cargo-muted mb-3">Success vs Failure</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} angle={-35} textAnchor="end" height={60} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0' }} />
                <Legend />
                <Bar dataKey="success" stackId="a" fill="#4ade80" name="Passed" radius={[2, 2, 0, 0]} />
                <Bar dataKey="failure" stackId="a" fill="#f87171" name="Failed" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-cargo-surface rounded-xl border border-cargo-border p-4">
            <h3 className="text-sm font-medium text-cargo-muted mb-3">DQ Score Trend</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} angle={-35} textAnchor="end" height={60} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} domain={[0, 100]} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0' }} />
                <Legend />
                <Line type="monotone" dataKey="dq_score" stroke="#60a5fa" strokeWidth={2} dot={{ r: 3 }} name="DQ Score %" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-cargo-surface rounded-xl border border-cargo-border p-1 w-fit">
        {(['detail', 'summary'] as const).map((tab) => (
          <button key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab ? 'bg-cargo-accent text-white' : 'text-cargo-muted hover:text-cargo-text'
            }`}>
            {tab === 'detail' ? 'Detail Report' : 'Group Summary'}
          </button>
        ))}
      </div>

      {/* ── Detail Report Tab ── */}
      {activeTab === 'detail' && (
        <>
          {/* Filters */}
          <div className="bg-cargo-surface rounded-xl border border-cargo-border p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-cargo-muted" />
              <span className="text-sm font-medium text-cargo-muted">Filters</span>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              {/* Group Name */}
              <div className="flex flex-col gap-1">
                <label className="text-xs text-cargo-muted">Group Name</label>
                <select value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)}
                  className="pl-3 pr-8 py-2 rounded-lg bg-cargo-panel border border-cargo-border text-cargo-text text-sm outline-none focus:border-cargo-accent cursor-pointer min-w-[160px]">
                  <option value="">All Groups</option>
                  {filterLists.groups.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>

              {/* Data Asset */}
              <div className="flex flex-col gap-1">
                <label className="text-xs text-cargo-muted">Data Asset</label>
                <select value={filterAsset} onChange={(e) => setFilterAsset(e.target.value)}
                  className="pl-3 pr-8 py-2 rounded-lg bg-cargo-panel border border-cargo-border text-cargo-text text-sm outline-none focus:border-cargo-accent cursor-pointer min-w-[160px]">
                  <option value="">All Data Assets</option>
                  {filterLists.assets.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>

              {/* Environment */}
              <div className="flex flex-col gap-1">
                <label className="text-xs text-cargo-muted">Environment</label>
                <select value={filterEnvironment} onChange={(e) => setFilterEnvironment(e.target.value)}
                  className="pl-3 pr-8 py-2 rounded-lg bg-cargo-panel border border-cargo-border text-cargo-text text-sm outline-none focus:border-cargo-accent cursor-pointer min-w-[140px]">
                  <option value="">All Environments</option>
                  {filterLists.environments.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>

              {/* Checkpoint */}
              <div className="flex flex-col gap-1">
                <label className="text-xs text-cargo-muted">Checkpoint</label>
                <select value={filterCheckpoint} onChange={(e) => setFilterCheckpoint(e.target.value)}
                  className="pl-3 pr-8 py-2 rounded-lg bg-cargo-panel border border-cargo-border text-cargo-text text-sm outline-none focus:border-cargo-accent cursor-pointer min-w-[160px]">
                  <option value="">All Checkpoints</option>
                  {filterLists.checkpoints.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Time Frame */}
              <div className="flex flex-col gap-1">
                <label className="text-xs text-cargo-muted flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Time Frame
                </label>
                <select value={filterTimeFrame} onChange={(e) => setFilterTimeFrame(e.target.value)}
                  className="pl-3 pr-8 py-2 rounded-lg bg-cargo-panel border border-cargo-border text-cargo-text text-sm outline-none focus:border-cargo-accent cursor-pointer min-w-[160px]">
                  {TIME_FRAME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              {/* Custom Date Range — only when time frame is "custom" */}
              {filterTimeFrame === 'custom' && (
                <>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-cargo-muted">Start Date</label>
                    <input type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)}
                      className="px-3 py-2 rounded-lg bg-cargo-panel border border-cargo-border text-cargo-text text-sm outline-none focus:border-cargo-accent cursor-pointer" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-cargo-muted">End Date</label>
                    <input type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)}
                      className="px-3 py-2 rounded-lg bg-cargo-panel border border-cargo-border text-cargo-text text-sm outline-none focus:border-cargo-accent cursor-pointer" />
                  </div>
                </>
              )}

              {/* Apply + Reset buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setAppliedFilters({
                      group: filterGroup, asset: filterAsset, environment: filterEnvironment,
                      checkpoint: filterCheckpoint, timeFrame: filterTimeFrame,
                      startDate: filterStartDate, endDate: filterEndDate,
                    });
                    setDetailPage(1);
                    setSummaryPage(1);
                  }}
                  disabled={!filtersChanged}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filtersChanged
                      ? 'bg-cargo-accent text-white hover:bg-cargo-accent/90'
                      : 'bg-cargo-panel border border-cargo-border text-cargo-muted cursor-not-allowed'
                  }`}>
                  Apply
                </button>
                {(appliedFilters.group || appliedFilters.asset || appliedFilters.environment ||
                  appliedFilters.checkpoint || appliedFilters.timeFrame !== 'last30' ||
                  appliedFilters.startDate || appliedFilters.endDate) && (
                  <button
                    onClick={() => {
                      setFilterGroup(''); setFilterAsset(''); setFilterEnvironment('');
                      setFilterCheckpoint(''); setFilterTimeFrame('last30');
                      setFilterStartDate(''); setFilterEndDate('');
                      setAppliedFilters({ group: '', asset: '', environment: '', checkpoint: '', timeFrame: 'last30', startDate: '', endDate: '' });
                      setDetailPage(1); setSummaryPage(1);
                    }}
                    className="p-2 rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel transition-colors"
                    title="Reset all filters">
                    <RotateCcw className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Active filters summary */}
            {(appliedFilters.group || appliedFilters.asset || appliedFilters.environment || appliedFilters.checkpoint) && (
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-cargo-border/50">
                <span className="text-xs text-cargo-muted">Active:</span>
                {appliedFilters.group && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-cargo-accent/10 text-cargo-accent border border-cargo-accent/20">
                    Group: {appliedFilters.group}
                    <button onClick={() => {
                      setFilterGroup('');
                      setAppliedFilters(f => ({ ...f, group: '' }));
                      setDetailPage(1);
                    }}><X className="w-3 h-3" /></button>
                  </span>
                )}
                {appliedFilters.asset && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                    Asset: {appliedFilters.asset}
                    <button onClick={() => {
                      setFilterAsset('');
                      setAppliedFilters(f => ({ ...f, asset: '' }));
                      setDetailPage(1);
                    }}><X className="w-3 h-3" /></button>
                  </span>
                )}
                {appliedFilters.environment && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">
                    Env: {appliedFilters.environment}
                    <button onClick={() => {
                      setFilterEnvironment('');
                      setAppliedFilters(f => ({ ...f, environment: '' }));
                      setDetailPage(1);
                    }}><X className="w-3 h-3" /></button>
                  </span>
                )}
                {appliedFilters.checkpoint && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    Checkpoint: {appliedFilters.checkpoint}
                    <button onClick={() => {
                      setFilterCheckpoint('');
                      setAppliedFilters(f => ({ ...f, checkpoint: '' }));
                      setDetailPage(1);
                    }}><X className="w-3 h-3" /></button>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Runs table */}
          <div className="bg-cargo-surface rounded-xl border border-cargo-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-cargo-border">
                    {([
                      ['data_asset', 'Data Asset', true],
                      ['group_name', 'Group', true],
                      ['checkpoint', 'Checkpoint', true],
                      ['run_date', 'Run Date', true],
                      ['success', 'Passed', true],
                      ['failed', 'Failed', true],
                      ['dq_score', 'DQ Score', true],
                      ['', '', false],
                    ] as [string, string, boolean][]).map(([col, label, sortable], i) => (
                      <th key={i}
                        className={`px-4 py-3 text-left text-xs font-medium text-cargo-muted uppercase tracking-wider
                                    ${sortable ? 'cursor-pointer hover:text-cargo-text select-none' : ''}`}
                        onClick={sortable ? () => handleDetailSort(col) : undefined}>
                        <div className="flex items-center gap-1">
                          {label}
                          {sortable && col && <SortIcon col={col} activeCol={detailSortCol} activeDir={detailSortDir} />}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detailLoading ? (
                    <tr><td colSpan={8} className="py-16 text-center">
                      <Loader2 className="w-5 h-5 animate-spin text-cargo-accent mx-auto" />
                      <p className="text-cargo-muted text-sm mt-2">Loading runs...</p>
                    </td></tr>
                  ) : detailRuns.length === 0 ? (
                    <tr><td colSpan={8} className="py-16 text-center text-cargo-muted text-sm">No validation runs found.</td></tr>
                  ) : (
                    detailRuns.map((run, idx) => (
                      <tr key={idx} className="border-b border-cargo-border/50 hover:bg-cargo-panel/50 transition-colors">
                        <td className="px-4 py-3 text-cargo-text text-sm max-w-[200px] truncate" title={run.data_asset}>{run.data_asset || '—'}</td>
                        <td className="px-4 py-3 text-cargo-muted whitespace-nowrap">{run.group_name}</td>
                        <td className="px-4 py-3 text-cargo-muted text-xs max-w-[160px] truncate" title={run.checkpoint}>{run.checkpoint || '—'}</td>
                        <td className="px-4 py-3 text-cargo-muted whitespace-nowrap">{formatDateTime(run.run_date)}</td>
                        <td className="px-4 py-3 text-green-400 text-center font-medium">{run.success}</td>
                        <td className="px-4 py-3 text-center font-medium" style={{ color: run.failed > 0 ? '#f87171' : '#94a3b8' }}>{run.failed}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded border ${scoreBg(run.dq_score)}`}>
                            {(run.dq_score || 0).toFixed(1)}%
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => handleViewReport(run)}
                            className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-accent hover:bg-cargo-accent/10 transition-colors"
                            title="View validation report">
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {!detailLoading && detailRuns.length > 0 && (
              <PaginationBar page={detailPage} totalPages={detailPages} perPage={detailPerPage}
                onPageChange={setDetailPage} onPerPageChange={(n) => { setDetailPerPage(n); setDetailPage(1); }} />
            )}
          </div>
        </>
      )}

      {/* ── Group Summary Tab ── */}
      {activeTab === 'summary' && (
        <div className="bg-cargo-surface rounded-xl border border-cargo-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-cargo-border">
                  <th className="px-4 py-3 text-left text-xs font-medium text-cargo-muted uppercase tracking-wider">Seq#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-cargo-muted uppercase tracking-wider">Group Name</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-cargo-muted uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-cargo-muted uppercase tracking-wider">Total Runs</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-cargo-muted uppercase tracking-wider">Last Run</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-cargo-muted uppercase tracking-wider">DQ Score</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-cargo-muted uppercase tracking-wider">B&R Score</th>
                </tr>
              </thead>
              <tbody>
                {summaryLoading ? (
                  <tr><td colSpan={7} className="py-16 text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-cargo-accent mx-auto" />
                    <p className="text-cargo-muted text-sm mt-2">Loading summary...</p>
                  </td></tr>
                ) : groupSummary.length === 0 ? (
                  <tr><td colSpan={7} className="py-16 text-center text-cargo-muted text-sm">No group data found.</td></tr>
                ) : (
                  groupSummary.map((g) => {
                    const isValidation = g.type === 'Validation' || g.type === 'V';
                    const score = isValidation ? g.validation_score : g.controlpoint_score;
                    return (
                      <tr key={g.id || g.group_name} className="border-b border-cargo-border/50 hover:bg-cargo-panel/50 transition-colors">
                        <td className="px-4 py-3 text-cargo-muted text-center">{g.sequence_number}</td>
                        <td className="px-4 py-3 text-cargo-text font-medium">{g.group_name}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${
                            isValidation ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-purple-500/20 text-purple-400 border-purple-500/30'
                          }`}>
                            {isValidation ? 'Validation' : 'Reconciliation'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-cargo-text text-center">{g.total_runs}</td>
                        <td className="px-4 py-3 text-cargo-muted whitespace-nowrap">{formatDateTime(g.last_run)}</td>
                        <td className="px-4 py-3 text-center">
                          {isValidation && score != null ? (
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded border ${scoreBg(score)}`}>
                              {score.toFixed(1)}%
                            </span>
                          ) : <span className="text-cargo-muted/40 text-xs">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {!isValidation && score != null ? (
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded border ${scoreBg(score)}`}>
                              {score.toFixed(1)}%
                            </span>
                          ) : <span className="text-cargo-muted/40 text-xs">—</span>}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {!summaryLoading && groupSummary.length > 0 && summaryPages > 1 && (
            <PaginationBar page={summaryPage} totalPages={summaryPages} perPage={10}
              onPageChange={setSummaryPage} onPerPageChange={() => {}} />
          )}
        </div>
      )}

      {/* Validation Report Modal */}
      {reportResultId && reportMeta && (
        <ValidationReportModal
          resultId={reportResultId}
          projectName={reportMeta.projectName}
          groupName={reportMeta.groupName}
          onClose={() => { setReportResultId(null); setReportMeta(null); }}
        />
      )}
    </div>
  );
};

export default InsightsPage;
